import * as angular from "angular"
import { IFertiliserProduct, IManureType, isNumber, isUndefinedOrNull, StockType } from "../omnia"
import { IFertiliserAnalysis, IFertiliserStock, IManureStock, INutritionPlanProductApp, ManureAnalysisProvider } from "../types"
import { IStockNutrient } from "./types"
import { getNutrientHorizontalBarBackgroundStyle } from "@omnia/shared/nutrition/colors";

export class StockVm {

    static createFromFertiliserStock(fertiliserStock: IFertiliserStock) {

        var vm = new StockVm();

        vm.fertiliserStock = fertiliserStock;
        vm.fertiliser = fertiliserStock.StandardProduct;

        vm._analysis = fertiliserStock.Custom ? fertiliserStock : vm.fertiliser;

        return vm;

    }

    static createFromFertiliser(fertiliser: IFertiliserProduct) {

        var vm = new StockVm();

        vm.fertiliser = fertiliser;

        vm._analysis = vm.fertiliser;

        return vm;

    }

    static createFromManureStock(manureAnalysis: ManureAnalysisProvider, manureStock: IManureStock) {

        var vm = new StockVm();

        vm.manureStock = manureStock;
        vm.manureType = manureStock.ManureType;

        vm._analysis = manureAnalysis(manureStock)

        return vm;

    }

    static createFromManureType(manureAnalysis: ManureAnalysisProvider, manureType: IManureType) {

        var vm = new StockVm();

        vm.manureType = manureType;

        var stock: IManureStock = {
            StockId: 0,
            StockType: StockType.Manure,
            ManureType: manureType
        };

        vm._analysis = manureAnalysis(stock)

        return vm;

    }

    public fertiliserStock: IFertiliserStock;
    private fertiliser: IFertiliserProduct;

    private manureStock: IManureStock;
    public manureType: IManureType;

    private _analysis: IFertiliserAnalysis;

    public nutrients: IStockNutrient[];

    isNewStock(): boolean {
        return !this.fertiliserStock && !this.manureStock;
    }

    hasStock(): boolean {
        return (!!this.fertiliserStock) || (!!this.manureStock);
    }

    isFertiliser(): boolean {
        return (!!this.fertiliserStock || !!this.fertiliser);
    }

    isManure(): boolean {
        return (!!this.manureStock || !!this.manureType);
    }

    isLiquid(): boolean {
        return (this._analysis && this._analysis.Liquid);
    }

    isSolid() {
        return !this.isLiquid()
    }

    get analysis(): IFertiliserAnalysis {
        return this._analysis;
    }

    get stockId(): number {
        if (this.hasStock()) {
            return this.isFertiliser() ? this.fertiliserStock.FertiliserStockId : this.manureStock.ManureStockId;
        }
    }

    get fertiliserStockId() {
        return this.fertiliserStock ? this.fertiliserStock.FertiliserStockId : 0
    }

    get manureStockId() {
        return this.manureStock ? this.manureStock.ManureStockId : 0
    }

    get actualStock(): (IFertiliserStock | IManureStock) {
        return this.fertiliserStock || this.manureStock;
    }

    get fertiliserProductId(): number {
        if (this.fertiliser) return this.fertiliser.FertiliserProductId;
    }

    get manureTypeId(): number {
        if (this.manureType) return this.manureType.ManureTypeId;
    }

    get hidden() {

        const { manureStock, fertiliser } = this

        if (manureStock) {
            return manureStock.Hidden
        }

        if (fertiliser && this.isNewStock()) {
            return fertiliser.Deleted
        }

        return false

    }

    checkId(fertiliserId: number, manureId: number) {
        return (this.manureStockId == (manureId || 0)) && (this.fertiliserStockId == (fertiliserId || 0))
    }

    get description(): string {

        if (this.fertiliserStock) {
            return this.fertiliserStock.Name;
        }

        if (this.fertiliser) {
            return this.fertiliser.Name;
        }

        if (this.manureStock) {
            return this.manureStock.Name;
        }

        if (this.manureType) {
            return this.manureType.Name;
        }

    }

    getAmountInStock(): number {

        if (this.fertiliserStock) {
            return Math.round(this.fertiliserStock.TotalAmount);
        }

        if (this.manureStock) {
            return Math.round(this.manureStock.TotalAmount * 10) / 10;
        }

    }

    setupNutrients(nutrients: IStockNutrient[]) {

        var analysis: IFertiliserAnalysis = this.analysis;
        this.nutrients = null;

        if (analysis) {

            this.nutrients = nutrients.map(n => {

                var c = angular.extend({}, n);

                if (n.valueProvider) {
                    c.value = n.valueProvider(analysis);
                    c.valueText = isUndefinedOrNull(c.value) ? "" : c.value.toLocaleString();
                }
                else {

                    c.value = analysis[n.key];

                    if (isNumber(c.value)) {
                        let text = (Math.round(c.value * 1000) / 10).toLocaleString() + " %";
                        c.valueText = text
                    }

                }

                return c;

            });

        }
    }

    getNutrientValue(nutrient: string): number {
        if (this._analysis) {
            return this._analysis[nutrient];
        }
    }

    getNutrientStyle(n: IStockNutrient): any {
        return getNutrientHorizontalBarBackgroundStyle(n.key, n.value, n.valueMax);
    }


    public updateProductApp(model: INutritionPlanProductApp, includeReferences: boolean) {

        var s = this;

        if (s.isManure()) {
            model.FertiliserId = null;
            model.Fertiliser = null;
        }
        else {
            model.ManureId = null;
            model.Manure = null;
        }

        if (s.fertiliserStock) {

            model.FertiliserId = s.fertiliserStock.FertiliserStockId;
            model.Fertiliser = (includeReferences ? s.fertiliserStock : null);

        }

        else if (s.manureStock) {

            model.ManureId = s.manureStock.ManureStockId;
            model.Manure = (includeReferences ? s.manureStock : null);

        }

        else if (s.fertiliser) {
            model.FertiliserId = null;
            model.Fertiliser = {
                StockId: 0,
                StockType: StockType.Fertiliser,
                StandardProductId: s.fertiliser.FertiliserProductId,
                StandardProduct: (includeReferences ? s.fertiliser : null),
                Name: s.fertiliser.Name
            }

        }

        else if (s.manureType) {
            model.ManureId = null;
            model.Manure = {
                StockId: 0,
                StockType: StockType.Manure,
                ManureTypeId: s.manureType.ManureTypeId,
                ManureType: (includeReferences ? s.manureType : null),
                Name: s.manureType.Name
            }

        }

    }

}
